<template>
  <div
    v-if="isVisible"
    class="modal-overlay"
    :class="{ 'modal-closing': isClosing }"
    @click="handleOverlayClick"
    :data-testid="testId"
  >
    <div
      class="modal-container"
      :class="{ 'modal-mobile': isMobile }"
      @click.stop
    >
      <!-- Header Section -->
      <div class="modal-header">
        <h3 v-if="title">{{ title }}</h3>
        <button @click="closeModal" class="close-btn">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <!-- Body Section -->
      <div class="modal-body">
        <slot name="body"></slot>
      </div>

      <!-- Footer Section -->
      <div class="modal-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Modal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    closeOnOverlay: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    testId: {
      type: String,
      default: "modal",
    },
  },
  data() {
    return {
      isMobile: false,
      isClosing: false,
    };
  },
  computed: {
    isVisible() {
      return this.visible;
    },
  },
  mounted() {
    this.checkMobile();
    window.addEventListener("resize", this.checkMobile);
    window.addEventListener("keydown", this.handleEscape);
    // Prevent body scroll when modal is open
    if (this.isVisible) {
      document.body.style.overflow = "hidden";
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkMobile);
    // Restore body scroll
    document.body.style.overflow = "";
    window.removeEventListener("keydown", this.handleEscape);
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.closeModal();
      }
    },
    closeModal() {
      this.isClosing = true;
      // Wait for animation to complete before emitting close
      setTimeout(() => {
        this.$emit("close");
        this.isClosing = false;
      }, 500); // Match animation duration + 1s
    },
    handleEscape(event) {
      if (event.key === "Escape" && this.isVisible) {
        this.closeModal();
      }
    },
  },
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: bgIn 0.3s ease-in forwards;
}

.modal-container {
  background: transparent;
  border-radius: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  max-height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Mobile Styles */
.modal-mobile {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 80%;
  max-width: 100%;
  max-height: 80%;
  border-radius: 16px 16px 0 0;
  animation: slideUp 0.3s ease-out;
}

.modal-closing.modal-overlay {
  animation: bgOut ease-in 0.3s forwards;
}
.modal-closing .modal-mobile {
  animation: slideDown 0.3s ease-in forwards;
}

.modal-header {
  background-color: white;
  color: #3b4143;
  padding: 16px;
  position: relative;
  border-bottom: 1px solid #d1d5db;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}
@keyframes bgIn {
  from {
    background-color: rgba(0, 0, 0, 0);
  }
  to {
    background-color: rgba(0, 0, 0, 0.5);
  }
}

@keyframes bgOut {
  from {
    background-color: rgba(0, 0, 0, 0.5);
  }
  to {
    background-color: rgba(0, 0, 0, 0);
  }
}

@keyframes slideDown {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
    display: none;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: scale(1);
  }

  to {
    opacity: 0;
    transform: scale(0);
    display: none;
  }
}

.modal-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
}

.close-btn {
  position: absolute;
  top: 50%;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #3b4143;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  font-weight: 900;
  transform: translateY(-50%);
}

.close-btn:hover {
  color: #495057;
}

.modal-body {
  padding: 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  background: white;
}

.modal-footer {
  padding: 16px;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 10px 0 rgba(0, 0, 0, 0.15);
  background: white;
}

/* Mobile footer sticky positioning */
@media (max-width: 768px) {
  .modal-footer {
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid #e5e7eb;
  }
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Desktop Styles */
@media (min-width: 769px) {
  .modal-header {
    background-color: #00a2e1;
    color: white;
    border-bottom: none;
    padding: 16px 24px;
  }

  .modal-header h3 {
    font-size: 16px;
  }

  .modal-body {
    padding: 24px;
    max-height: 350px;
  }

  .close-btn {
    color: white;
  }

  .close-btn:hover {
    color: #f2f2f2;
  }

  .modal-footer {
    box-shadow: none;
    padding: 16px 24px;
  }

  .modal-container {
    width: auto;
    min-width: 678px;
    position: relative;
    animation: fadeIn 0.3s ease-out;
    border-radius: 16px;
    box-shadow: none;
  }

  .modal-closing .modal-container {
    animation: fadeOut 0.3s ease-in forwards;
  }
}
</style>
